import { NotFound } from '@common/components/feedback/NotFound';
import { Layout } from '@common/components/layout/Layout';
import { AzureCallback, LoginPage, ProtectedRoute } from '@features/auth';
import { Spinner } from '@ntconsult/aluma-react';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v7';
import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';

// Páginas autenticadas — lazy (code-splitting por rota). Cada arquivo `export default`.
const PerfilPage = lazy(() => import('@features/auth/components/PerfilPage'));
const MeuProgressoPage = lazy(() => import('@features/catalogo/components/MeuProgressoPage'));
const CatalogoCursosPage = lazy(() => import('@features/catalogo/components/CatalogoCursosPage'));
const CatalogoTrilhasPage = lazy(() => import('@features/catalogo/components/CatalogoTrilhasPage'));
const CursoDetailPage = lazy(() => import('@features/catalogo/components/CursoDetailPage'));
const TrilhaDetailPage = lazy(() => import('@features/catalogo/components/TrilhaDetailPage'));
const PlayerAulaPage = lazy(() => import('@features/catalogo/components/PlayerAulaPage'));
const MeusCertificadosPage = lazy(
  () => import('@features/certificado/components/MeusCertificadosPage'),
);
const BuscaResultadosPage = lazy(() => import('@features/busca/components/BuscaResultadosPage'));
const MeusCursosPage = lazy(() => import('@features/autoria/components/MeusCursosPage'));
const EditorCursoPage = lazy(() => import('@features/autoria/components/EditorCursoPage'));
const TrilhasCuradoriaPage = lazy(
  () => import('@features/curadoria/components/TrilhasCuradoriaPage'),
);
const EditorTrilhaPage = lazy(() => import('@features/curadoria/components/EditorTrilhaPage'));
const DashboardProfessorPage = lazy(
  () => import('@features/dashboard/components/DashboardProfessorPage'),
);
const DashboardCuradorPage = lazy(
  () => import('@features/dashboard/components/DashboardCuradorPage'),
);
const DashboardAdminPage = lazy(() => import('@features/dashboard/components/DashboardAdminPage'));
const GestaoUsuariosPage = lazy(() => import('@features/usuarios/components/GestaoUsuariosPage'));
const CertificateVerifyPage = lazy(
  () => import('@features/certificado/components/CertificateVerifyPage'),
);

function RootLayout() {
  return (
    <NuqsAdapter>
      <Suspense fallback={<Spinner layout="fullPage" />}>
        <Outlet />
      </Suspense>
    </NuqsAdapter>
  );
}

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/auth/azure/callback', element: <AzureCallback /> },
      // Verificação pública de certificado — sem sessão (SPEC 6.2).
      { path: '/certificados/:id', element: <CertificateVerifyPage /> },
      {
        element: <ProtectedRoute />,
        children: [
          {
            element: <Layout />,
            children: [
              { index: true, element: <Navigate to="/meu-progresso" replace /> },
              // Aluno (todo usuário autenticado é aluno por padrão).
              { path: 'meu-progresso', element: <MeuProgressoPage /> },
              { path: 'catalogo/cursos', element: <CatalogoCursosPage /> },
              { path: 'catalogo/trilhas', element: <CatalogoTrilhasPage /> },
              { path: 'cursos/:id', element: <CursoDetailPage /> },
              { path: 'trilhas/:id', element: <TrilhaDetailPage /> },
              { path: 'aulas/:id', element: <PlayerAulaPage /> },
              { path: 'meus-certificados', element: <MeusCertificadosPage /> },
              { path: 'busca', element: <BuscaResultadosPage /> },
              { path: 'perfil', element: <PerfilPage /> },
              // Autor (professor) — admin contorna o gate.
              {
                element: <ProtectedRoute allowedRoles={['professor']} />,
                children: [
                  { path: 'autoria/cursos', element: <MeusCursosPage /> },
                  { path: 'autoria/cursos/:id', element: <EditorCursoPage /> },
                  { path: 'painel/professor', element: <DashboardProfessorPage /> },
                ],
              },
              // Curador.
              {
                element: <ProtectedRoute allowedRoles={['curador']} />,
                children: [
                  { path: 'curadoria/trilhas', element: <TrilhasCuradoriaPage /> },
                  { path: 'curadoria/trilhas/:id', element: <EditorTrilhaPage /> },
                  { path: 'painel/curador', element: <DashboardCuradorPage /> },
                ],
              },
              // Admin.
              {
                element: <ProtectedRoute allowedRoles={['admin']} />,
                children: [
                  { path: 'painel/admin', element: <DashboardAdminPage /> },
                  { path: 'admin/usuarios', element: <GestaoUsuariosPage /> },
                ],
              },
            ],
          },
        ],
      },
      { path: '*', element: <NotFound /> },
    ],
  },
]);
